import { z } from "zod"

export const PaidPlanSchema = z.enum([
	"starter",
	"pro",
	"scale",
	"business",
	"enterprise",
])
export type PaidPlan = z.infer<typeof PaidPlanSchema>
export type Plan = "free" | PaidPlan
export const PlanIntervalSchema = z.enum(["monthly", "yearly"])
export type PlanInterval = z.infer<typeof PlanIntervalSchema>
export const planHierarchy: Array<Plan> = [
	"free",
	"starter",
	"pro",
	"scale",
	"business",
	"enterprise",
]

export const hasLegacyPlan = (plan: Plan) =>
	!!["free", "business", "enterprise"].includes(plan)

export const hasRequiredPlans = (
	plan: Plan | null | undefined,
	required: Array<Plan>,
) => !!plan && required.includes(plan)

export const FREE_PLAN_MEETING_LIMIT_TEXT =
	"The free plan includes ten recorded meetings or uploaded videos per month."
export const FREE_PLAN_LINK_LIMIT = 100
export const FREE_PLAN_LINK_LIMIT_TEXT =
	"The free plan includes one hundred total note links. A link represents a connection between a note and a project/issue/tag. Notes are always unlimited, note links are unlimited from the Pro plan and up."

export const free = {
	only: planHierarchy[0],
	andUp: planHierarchy.slice(0),
	andDown: planHierarchy.slice(0, 1),
}

export const starter = {
	only: planHierarchy[1],
	andUp: planHierarchy.slice(1),
	andDown: planHierarchy.slice(0, 2),
}

export const pro = {
	only: planHierarchy[2],
	andUp: planHierarchy.slice(2),
	andDown: planHierarchy.slice(0, 3),
}

export const scale = {
	only: planHierarchy[3],
	andUp: planHierarchy.slice(3),
	andDown: planHierarchy.slice(0, 4),
}

export const business = {
	only: planHierarchy[4],
	andUp: planHierarchy.slice(4),
	andDown: planHierarchy.slice(0, 5),
}

export const enterprise = {
	only: planHierarchy[5],
	andUp: planHierarchy.slice(5),
	andDown: planHierarchy.slice(0, 6),
}

// One gate for the dashboard button and both OAuth handlers; they drifted apart once.
export const HUBSPOT_REQUIRED_PLANS = scale.andUp
export const HUBSPOT_PLAN_REQUIRED_REASON = "plan-required"
export const ATTIO_REQUIRED_PLANS = scale.andUp

export const SUPPORT_PORTAL_PUBLISH_REQUIRED_PLANS = scale.andUp

// Configuring the Support Portal is open to every plan; making it public is not.
// A portal that is already live stays togglable so a downgrade cannot trap it on.
export const canPublishSupportPortal = ({
	plan,
	isCurrentlyPublished,
}: {
	plan: Plan | null | undefined
	isCurrentlyPublished: boolean
}): boolean =>
	isCurrentlyPublished ||
	hasRequiredPlans(plan, SUPPORT_PORTAL_PUBLISH_REQUIRED_PLANS)

export const BRANDING_REMOVAL_REQUIRED_PLANS = scale.andUp

// The Startup program bills as Scale and a trial has not paid yet, so neither
// buys the right to drop the Productlane branding.
export const canHideBranding = ({
	plan,
	isStartupProgram,
	isTrial = false,
}: {
	plan: Plan | null | undefined
	isStartupProgram: boolean
	isTrial?: boolean
}): boolean =>
	!isStartupProgram &&
	!isTrial &&
	hasRequiredPlans(plan, BRANDING_REMOVAL_REQUIRED_PLANS)

export const PLAN_DISPLAY_NAMES: Record<Plan, string> = {
	free: "Free",
	starter: "Starter",
	pro: "Pro",
	scale: "Scale",
	business: "Business",
	enterprise: "Enterprise",
} as const

// Total addresses a workspace may hold, primary included. Starter keeps a single
// address, so a second one is the first thing a Pro upgrade buys.
export const EMAIL_ADDRESS_LIMITS: Record<Plan, number> = {
	free: 0,
	starter: 1,
	pro: 10,
	scale: 25,
	business: 25,
	enterprise: 25,
} as const

// The Startup program bills as Scale but is capped well below it here.
export const STARTUP_EMAIL_ADDRESS_LIMIT = 3

export const resolveEmailAddressLimit = ({
	plan,
	isStartupProgram,
}: {
	plan: Plan | null | undefined
	isStartupProgram: boolean
}): number => {
	if (!plan) return 0
	if (isStartupProgram) return STARTUP_EMAIL_ADDRESS_LIMIT
	return EMAIL_ADDRESS_LIMITS[plan]
}

// Slack workspaces connected as support. Below Pro, Slack is not available at all.
export const SLACK_WORKSPACE_LIMITS: Record<Plan, number> = {
	free: 0,
	starter: 0,
	pro: 1,
	scale: 20,
	business: 20,
	enterprise: 20,
} as const

export const SLACK_WORKSPACE_LIMIT_REASON = "workspace-limit"

// Per-workspace exceptions to SLACK_WORKSPACE_LIMITS; a plan with a higher limit still wins.
const SLACK_WORKSPACE_LIMIT_OVERRIDES: Record<string, number> = {
	// Directus keeps one Slack workspace below Pro, agreed 2026-11-04 in
	// https://productlane.com/threads/0a65c5b3-2f1d-4ae8-aea2-0b773f03068f
	"b7eb8b75-b3d5-4767-8778-865548344459": 1,
}

export const resolveSlackWorkspaceLimit = (
	plan: Plan,
	workspaceId: string | null | undefined,
): number =>
	Math.max(
		SLACK_WORKSPACE_LIMITS[plan],
		(workspaceId && SLACK_WORKSPACE_LIMIT_OVERRIDES[workspaceId]) || 0,
	)

// After a downgrade the oldest connections within the limit keep syncing; the rest pause until an upgrade or a disconnect.
export const liveSlackConnectionIds = (
	connections: Array<{ id: string; createdAt: Date | number }>,
	plan: Plan,
	workspaceId: string | null | undefined,
): Set<string> =>
	new Set(
		[...connections]
			.sort(
				(a, b) =>
					Number(a.createdAt) - Number(b.createdAt) || a.id.localeCompare(b.id),
			)
			.slice(0, resolveSlackWorkspaceLimit(plan, workspaceId))
			.map((row) => row.id),
	)

export const FreeAiResolutions: Record<Plan, number> = {
	free: 0,
	starter: 25,
	pro: 50,
	scale: 100,
	business: 100,
	enterprise: 100,
} as const

// Resolutions a trial gets for the whole 7 days, whatever tier it is trialing.
// The monthly reset skips a trialing workspace, so this allowance is spent once
// and only clears when the workspace pays.
export const TRIAL_AI_RESOLUTIONS = 50

// Free resolutions for a workspace. A trial gets one flat allowance for the
// whole trial; everyone else gets their plan's monthly tier. The vetted startup
// program bills as Scale, so it keeps the plan tier.
export const freeAiResolutionsFor = ({
	plan,
	trialing = false,
	isStartup = false,
}: {
	plan: Plan
	trialing?: boolean
	isStartup?: boolean
}): number =>
	trialing && !isStartup ? TRIAL_AI_RESOLUTIONS : FreeAiResolutions[plan]

export const AI_COST_PER_RESOLUTION_CENTS = 79

export const STARTUP_PROGRAM_MAX_SEATS = 6
export const STARTUP_PROGRAM_DISPLAY_NAME = "Startup program"
export const STARTUP_PROGRAM_CONTRACT_LENGTH_MONTHS = 12
